import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnifiedCard } from '../../components';
import { RenameDataCollectionModal } from '../modal';
import {
    canManageDataCollectionSummary,
    deleteCollectDataCollection,
    patchCollectDataCollection,
    withdrawMyCollectContribution,
    type RecentCollectCollectionSummary,
} from '../../services/api/collectDataService';

export type DataCollectLibraryCardProps = {
    col: RecentCollectCollectionSummary;
    index: number;
    ownerLabel: string;
    accessToken: string | undefined;
    onRefresh: () => void;
};

const DataCollectLibraryCard: React.FC<DataCollectLibraryCardProps> = ({ col, index, ownerLabel, accessToken, onRefresh }) => {
    const { t } = useTranslation();
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const status = String(col.status).toLowerCase();
    const processStatus = status === 'closed' ? 'completed' : status === 'draft' ? 'new' : 'ongoing';
    const previewRaw = (col.business_objective_preview && String(col.business_objective_preview).trim()) || '';
    const descriptionLine =
        previewRaw ||
        t('views.processView.homeDataCollectKind', { defaultValue: 'Structured responses' });

    const canManage = canManageDataCollectionSummary(col);
    const isContributorOnly = Boolean(col.opened_as_contributor);

    const openRename = useCallback(() => {
        if (!accessToken || !canManage) return;
        setRenameModalOpen(true);
    }, [accessToken, canManage]);

    const executeRename = useCallback(
        async (trimmed: string): Promise<boolean> => {
            if (!accessToken || !canManage) return false;
            const res = await patchCollectDataCollection(col.id, { name: trimmed }, accessToken);
            if (res.status === 200) {
                onRefresh();
                return true;
            }
            return false;
        },
        [accessToken, canManage, col.id, onRefresh],
    );

    return (
        <>
            <UnifiedCard
                variant="library"
                index={index}
                elementType="dataCollect"
                processName={col.name}
                processStatus={processStatus}
                thisProcessHasABadge={false}
                theBadgeHasAValue={false}
                badgeValue={0}
                thisProcessHasAOwner={true}
                owner={ownerLabel}
                route="/process/data-collect/"
                dataCollectContributorToken={
                    col.opened_as_contributor && col.contributor_invitation_token
                        ? col.contributor_invitation_token
                        : undefined
                }
                canRunOwnerMenuActions={canManage}
                canWithdrawDataCollectContribution={isContributorOnly && !canManage}
                onWithdrawDataCollectContribution={async () => {
                    if (!accessToken || !isContributorOnly) return false;
                    const res = await withdrawMyCollectContribution(col.id, accessToken);
                    if (res.status === 200) {
                        onRefresh();
                        return true;
                    }
                    return false;
                }}
                deleteProcess={async () => {
                    if (!accessToken || !canManage) return false;
                    const res = await deleteCollectDataCollection(col.id, accessToken);
                    if (res.status === 200) {
                        onRefresh();
                        return true;
                    }
                    return false;
                }}
                description={descriptionLine}
                processId={col.id}
                folder={false}
                setProject={() => {}}
                setProcess={() => {}}
                updatedAt={col.updated_at ?? undefined}
                createdAt={col.created_at ?? undefined}
                processSnapshot={{
                    status: col.status,
                    updated_at: col.updated_at,
                    created_at: col.created_at,
                }}
                onDataCollectRenameRequest={openRename}
                onDataCollectCoOwnerAdded={onRefresh}
                t={t}
            />
            {canManage && renameModalOpen ? (
                <RenameDataCollectionModal
                    collectionId={col.id}
                    initialName={col.name}
                    modalDomId={`famla-dc-rename-lib-${col.id}`}
                    autoShow
                    onHidden={() => setRenameModalOpen(false)}
                    onSave={executeRename}
                />
            ) : null}
        </>
    );
};

export default DataCollectLibraryCard;
